<template>
  <section class="shared-section referrals">
    <header class="shared-section-header">
      <h2 class="shared-section-header-heading">{{ $t('referrals.header.heading') }}</h2>
    </header>
    <!-- Intro -->
    <div class="mb-10 referrals-intro">
      <h3 class="mb-4 referrals-intro-heading">{{ $options.introHeading }}</h3>
      <h4 class="flex items-center mb-3 font-bold">
        {{ $options.howItWorks.heading }}
        <span
          v-if="!isInfluencer && false"
          class="flex items-center text-xs uppercase cursor-pointer text-ricoma-blue-100 hover:text-ricoma-blue-200"
          @click="showModal = true"
        >
          <VSvgIcon name="play" class="w-5 ml-2 mr-1" /> {{ $options.howItWorks.video.actionText }}
        </span>
      </h4>
      <div class="md:flex">
        <div class="md:w-5/12">
          <Video
            :thumbnail="$options.howItWorks.video.thumbnail"
            :video="$options.howItWorks.video.url"
          />
        </div>
        <div class="mt-5 md:w-7/12 md:px-8 md:mt-0">
          <ul>
            <li
              v-for="(item, index) in $options.howItWorks.list"
              :key="`how-it-works-${index}`"
              class="flex mb-4"
            >
              <span
                class="flex items-center justify-center flex-shrink-0 inline-block w-6 h-6 mr-2 font-bold text-white rounded-full bg-ricoma-orange-200"
              >
                {{ index + 1 }}
              </span>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Social -->

    <div class="mb-8 referrals-social">
      <div class="p-4 py-6">
        <h5 class="referrals-social-heading">
          {{ $t('referrals.social_customer_portal.heading') }}
        </h5>
        <div class="flex flex-wrap">
          <div class="referrals-social-copy">
            <div class="referrals-social-copy-link">
              {{ referralLink }}
            </div>
            <button class="referrals-social-copy-button">
              <font-awesome-icon :icon="['fas', 'link']" />
              <span class="ml-2 text-sm font-bold uppercase" v-clipboard="referralLink">
                {{ $t('referrals.social_customer_portal.btn') }}
              </span>
            </button>
          </div>
          <div class="referrals-social-icons">
            <div
              v-for="({ prefix, name, bg, link }, index) in $options.icons"
              :key="`social-${index}-${name}`"
              class="referrals-social-icons-icon"
              :style="{ backgroundColor: bg }"
            >
              <a
                class="flex items-center justify-center inline-block w-full h-full"
                :href="socialLink(link, referralLink)"
                target="_blank"
              >
                <font-awesome-icon :icon="[prefix, name]" class="text-2xl text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasDtgReward" class="p-4 py-6 border-t">
        <div class="flex flex-wrap justify-between mb-2">
          <div>
            <h5 class="referrals-social-heading">
              {{ $t('referrals.social_dtg_portal.heading') }}
            </h5>
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="referrals-social-copy">
            <div class="referrals-social-copy-link">
              {{ referralDtgLink }}
            </div>
            <button class="referrals-social-copy-button">
              <font-awesome-icon :icon="['fas', 'link']" />
              <span class="ml-2 text-sm font-bold uppercase" v-clipboard="referralDtgLink">
                {{ $t('referrals.social_shop_ricoma.btn') }}
              </span>
            </button>
          </div>
          <div class="referrals-social-icons">
            <div
              v-for="({ prefix, name, bg, link }, index) in $options.icons"
              :key="`social-shop-ricoma-${index}-${name}`"
              class="referrals-social-icons-icon"
              :style="{ backgroundColor: bg }"
            >
              <a
                class="flex items-center justify-center inline-block w-full h-full"
                :href="socialLink(link, referralDtgLink)"
                target="_blank"
              >
                <font-awesome-icon :icon="[prefix, name]" class="text-2xl text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="p-4 py-6 border-t">
        <div class="flex flex-wrap justify-between mb-2">
          <div>
            <h5 class="referrals-social-heading">
              {{ $t('referrals.social_dtp_portal.heading') }}
            </h5>
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="referrals-social-copy">
            <div class="referrals-social-copy-link">
              {{ referralDtpLink }}
            </div>
            <button class="referrals-social-copy-button">
              <font-awesome-icon :icon="['fas', 'link']" />
              <span class="ml-2 text-sm font-bold uppercase" v-clipboard="referralDtpLink">
                {{ $t('referrals.social_shop_ricoma.btn') }}
              </span>
            </button>
          </div>
          <div class="referrals-social-icons">
            <div
              v-for="({ prefix, name, bg, link }, index) in $options.icons"
              :key="`social-shop-ricoma-${index}-${name}`"
              class="referrals-social-icons-icon"
              :style="{ backgroundColor: bg }"
            >
              <a
                class="flex items-center justify-center inline-block w-full h-full"
                :href="socialLink(link, referralDtpLink, false)"
                target="_blank"
              >
                <font-awesome-icon :icon="[prefix, name]" class="text-2xl text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="p-4 py-6">
        <h5 class="referrals-social-heading">
          {{ $t('referrals.social_dtf_portal.heading') }}
        </h5>
        <div class="flex flex-wrap">
          <div class="referrals-social-copy">
            <div class="referrals-social-copy-link">
              {{ referralDtfLink }}
            </div>
            <button class="referrals-social-copy-button">
              <font-awesome-icon :icon="['fas', 'link']" />
              <span class="ml-2 text-sm font-bold uppercase" v-clipboard="referralDtfLink">
                {{ $t('referrals.social_dtf_portal.btn') }}
              </span>
            </button>
          </div>
          <div class="referrals-social-icons">
            <div
              v-for="({ prefix, name, bg, link }, index) in $options.icons"
              :key="`social-${index}-${name}`"
              class="referrals-social-icons-icon"
              :style="{ backgroundColor: bg }"
            >
              <a
                class="flex items-center justify-center inline-block w-full h-full"
                :href="socialLink(link, referralDtfLink, false)"
                target="_blank"
              >
                <font-awesome-icon :icon="[prefix, name]" class="text-2xl text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- <div class="p-4 py-6"> -->
      <!--   <h5 class="referrals-social-heading"> -->
      <!--     {{ $t('referrals.social_chroma_portal.heading') }} -->
      <!--   </h5> -->
      <!--   <div class="flex flex-wrap"> -->
      <!--     <div class="referrals-social-copy"> -->
      <!--       <div class="referrals-social-copy-link"> -->
      <!--         {{ referralChromaLink }} -->
      <!--       </div> -->
      <!--       <button class="referrals-social-copy-button"> -->
      <!--         <font-awesome-icon :icon="['fas', 'link']" /> -->
      <!--         <span -->
      <!--           class="ml-2 text-sm font-bold uppercase" -->
      <!--           @click="copyTextToClipboard(referralChromaLink)" -->
      <!--         > -->
      <!--           {{ $t('referrals.social_chroma_portal.btn') }} -->
      <!--         </span> -->
      <!--       </button> -->
      <!--     </div> -->
      <!--     <div class="referrals-social-icons"> -->
      <!--       <div -->
      <!--         v-for="({ prefix, name, bg, link }, index) in $options.icons" -->
      <!--         :key="`social-${index}-${name}`" -->
      <!--         class="referrals-social-icons-icon" -->
      <!--         :style="{ backgroundColor: bg }" -->
      <!--       > -->
      <!--         <a -->
      <!--           class="flex items-center justify-center inline-block w-full h-full" -->
      <!--           :href="socialLink(link, referralChromaLink, false)" -->
      <!--           target="_blank" -->
      <!--         > -->
      <!--           <font-awesome-icon :icon="[prefix, name]" class="text-2xl text-white" /> -->
      <!--         </a> -->
      <!--       </div> -->
      <!--     </div> -->
      <!--   </div> -->
      <!-- </div> -->

      <div class="p-4 py-6 border-t">
        <div class="flex flex-wrap justify-between mb-2">
          <div>
            <h5 class="referrals-social-heading">
              {{ $t('referrals.social_shop_ricoma.heading') }}
            </h5>
          </div>
          <div class="lg:mr-32">
            <span
              class="px-6 py-2 rounded-full cursor-pointer bg-ricoma-purple-400-soft text-ricoma-purple-400"
              @click="showProductsModal = true"
            >
              {{ $t('referrals.social_shop_ricoma.modal_btn') }}
            </span>
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="referrals-social-copy">
            <div class="referrals-social-copy-link">
              {{ referralShopRicomaLink }}
            </div>
            <button class="referrals-social-copy-button">
              <font-awesome-icon :icon="['fas', 'link']" />
              <span
                class="ml-2 text-sm font-bold uppercase"
                @click="copyTextToClipboard(referralShopRicomaLink)"
              >
                {{ $t('referrals.social_shop_ricoma.btn') }}
              </span>
            </button>
          </div>
          <div class="referrals-social-icons">
            <div
              v-for="({ prefix, name, bg, link }, index) in $options.icons"
              :key="`social-shop-ricoma-${index}-${name}`"
              class="referrals-social-icons-icon"
              :style="{ backgroundColor: bg }"
            >
              <a
                class="flex items-center justify-center inline-block w-full h-full"
                :href="socialLink(link, referralShopRicomaLink, false)"
                target="_blank"
              >
                <font-awesome-icon :icon="[prefix, name]" class="text-2xl text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Blocks -->
    <div class="referrals-stats">
      <div v-for="(referral, index) in referralStats" :key="`referral-stats-${index}`" class="mb-6">
        <h6 class="mb-0 referrals-stats-heading">
          <span>{{ referral.heading }}</span>
          <TooltipInfo
            v-if="referral.tooltip"
            :classes="{ popup: 'w-70 -mr-20 md:w-76 md:right-100' }"
            :content="referral.tooltip"
          />
        </h6>
        <h5 class="mb-2 text-sm font-normal">{{ referral.subheading }}</h5>
        <div
          class="referrals-stats-box"
          :class="[...referral.classes]"
          :style="{ backgroundColor: referral.color }"
        >
          <div class="referrals-stats-box-content">
            <div class="text-4xl font-semibold">
              <div
                class="flex items-center justify-center px-2"
                v-for="content in referral.content"
                :key="`referral-content-${content}`"
                v-html="content"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <TableReferrals
      toolbarHeading="referral history"
      :withDateRange="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="referrals"
      :getReferrals="fetchReferralStats"
    >
      <template #referrals-table-toolbar>
        <button class="shared-table-toolbar-button">
          <download-csv
            :data="csvData"
            :fields="$options.csvProps.fields"
            :labels="$options.csvProps.labels"
          >
            <div class="flex items-center">
              <VSvgIcon name="export" class="h-5 mr-1" />
              {{ $t('referrals.stats.table.csvBtn') }}
            </div>
          </download-csv>
        </button>
      </template>

      <template #table-data-6="trData">
        <span
          :class="[trData.referral_value < 0 ? 'text-ricoma-red-100' : 'text-ricoma-green-100']"
        >
          {{ trData['rewards amount'] }}
        </span>
      </template>

      <template #table-data-7="trData">
        <span class="font-bold">{{ trData['total balance'] }}</span>
      </template>
    </TableReferrals>

    <!-- How it works video -->
    <Modal :show="showModal" @close="showModal = false" width="95%" maxWidth="900px" theme="dark">
      <Video
        :thumbnail="$options.howItWorks.video.thumbnail"
        :video="$options.howItWorks.video.url"
      />
    </Modal>
    <!-- Products Eligible -->
    <Modal
      :show="showProductsModal"
      @close="showProductsModal = false"
      width="95%"
      maxWidth="900px"
      theme="dark"
    >
      <ReferralProductsEligible
        :productsEligible="productsEligible"
        @close="showProductsModal = false"
      />
    </Modal>
  </section>
</template>

<script>
import { parseISO, isBefore } from 'date-fns';
import { mapState } from 'vuex';

import {
  addDollarSymbol,
  isInfluencer,
  // typeCheck,
} from '@/utils';

import VSvgIcon from '@/components/base/VSvgIcon';
import Modal from '@/components/shared/modal/Modal';
import TooltipInfo from '@/components/shared/tooltip/TooltipInfo';
import Video from '@/components/customer/Video';
import ReferralProductsEligible from '@/components/customer/ReferralProductsEligible';
import TableReferrals from '@/components/shared/table/TableReferrals';

import { safeNullDataset, normalizeCustomerReferrals } from '@/utils';

// helper functions
const areCreditsFrozen = unredeemedAmount => {
  return parseFloat(unredeemedAmount) < 0;
};

/*
  KEEP HERE AS THEY'RE PLANNING IN IMPLEMENTING IT ON THE NEXT RELEASE

  const distributeCredits = (pendingCredits, availableCredits) => {
    // short circuit function if values are not numbers
    if (!typeCheck(pendingCredits).isNum || !typeCheck(availableCredits).isNum)
      return { pending: 0, available: 0 };

    // Only proceed if there's a negative pending credit we can use towards a positive available credit
    if (pendingCredits >= 0 || availableCredits <= 0)
      return { pending: pendingCredits, available: availableCredits };

    console.log({ pendingCredits, availableCredits });

    const balance = pendingCredits + availableCredits;
    // Set available credit to 0 where balance returns negative or
    // else add up any negative pending credits and adjust available balance
    const updatedAvailableCredits = balance < 0 ? 0 : balance;

    // Adjust pending balance to balance where if the result is negative or
    // reset it to 0 where it is entirely used to cover the available credit
    const updatedPendingCredits = balance < 0 ? balance : 0;

    return { pending: updatedPendingCredits, available: updatedAvailableCredits };
  };
  */

export default {
  name: 'Referrals',
  components: {
    TooltipInfo,
    VSvgIcon,
    Modal,
    Video,
    TableReferrals,
    ReferralProductsEligible,
  },
  data() {
    return {
      csvData: [],
      selectedPayout: {},
      showModal: false,
      showProductsModal: false,
      showThresholdColumn: false,
      productsEligible: [],
    };
  },
  computed: mapState({
    referrals: state => {
      return {
        data: normalizeCustomerReferrals(
          safeNullDataset(state)(['customer', 'referralStats', 'data']),
        ),
        meta: { ...state.customer?.referralStats?.meta?.meta },
      };
    },
    referralCode() {
      return this.$user.referral_code;
    },
    hasDtgReward() {
      return this.$user.referral_payout_tag.has_dtg_reward;
    },
    referralStats(state) {
      const {
        redeemedAmount,
        unredeemedAmount,
        referredCustomersCount,
        availableCredit,
      } = state.customer.referralStats;

      const isFrozen = areCreditsFrozen(unredeemedAmount);
      const titleType = isInfluencer(this.$user) ? 'title-influencer' : 'title-general';
      const contentType = isInfluencer(this.$user) ? 'content-influencer' : 'content-general';

      return [
        {
          heading: this.$t(`referrals.stats.blocks.b1.${titleType}`),
          subheading: isFrozen ? this.$t('referrals.stats.blocks.b1.subtitle') : '',
          tooltip: this.$t('referrals.stats.blocks.b1.tooltip'),
          content: [
            `<div>
            ${availableCredit ? addDollarSymbol(parseFloat(availableCredit).toFixed(2)) : '$0.00'}
            </div>`,
          ],
          color: '#3C2099',
          classes: isFrozen && 'opacity-25',
        },
        {
          heading: this.$t(`referrals.stats.blocks.b2.${titleType}`),
          subheading: this.$t('referrals.stats.blocks.b2.subtitle'),
          tooltip: this.$t('referrals.stats.blocks.b2.tooltip'),
          content: [
            `<div>
              ${
                unredeemedAmount
                  ? addDollarSymbol(parseFloat(unredeemedAmount).toFixed(2))
                  : '$0.00'
              }
            </div>`,
          ],
          color: isFrozen ? 'rgba(34, 187, 52, 0.3)' : '#22bb33',
          classes: isFrozen && 'text-black',
        },
        {
          heading: this.$t(`referrals.stats.blocks.b3.${titleType}`),
          subheading: this.$t('referrals.stats.blocks.b3.subtitle'),
          tooltip: this.$t('referrals.stats.blocks.b3.tooltip'),
          content: [
            `<div class="mb-1 text-sm font-bold text-black lg:text-base">
              <div>${this.$t(`referrals.stats.blocks.b3.${contentType}-1`)}</div>
              <div class="font-normal">${referredCustomersCount ?? 0}</div>
            </div>`,
            `<div class="text-sm font-bold text-black lg:text-base">
              <div>${this.$t(`referrals.stats.blocks.b3.${contentType}-2`)}</div>
              <div class="font-normal">
                ${redeemedAmount ? addDollarSymbol(parseFloat(redeemedAmount).toFixed(2)) : '$0.00'}
              </div>
            </div>`,
          ],
          color: 'transparent',
          classes: 'text-black text-base font-bold',
        },
      ];
    },
    referralLink() {
      return `https://info.ricoma.com/referral-emb?referralCode=${this.referralCode}`;
    },
    referralShopRicomaLink() {
      return `https://ricoma.com?referral=${this.referralCode}`;
    },
    referralDtpLink() {
      return `https://info.ricoma.com/referral-dtp?referralCode=${this.referralCode}`;
    },
    referralDtfLink() {
      return `https://info.ricoma.com/referral-dtf?referralCode=${this.referralCode}`;
    },
    referralChromaLink() {
      return `https://www.chromaembroidery.com/?referral=${this.referralCode}`;
    },
    referralDtgLink() {
      return `https://info.ricoma.com/referral-dtg?referralCode=${this.referralCode}`;
    },
    isInfluencer() {
      return isInfluencer(this.$user);
    },
  }),
  methods: {
    async copyTextToClipboard(text) {
      await navigator.clipboard.writeText(text);
    },
    handleSingleColspan({ is_last = false, overview_endDate = new Date(), type = '' }) {
      if (is_last && this.isDateBeforeToday(overview_endDate)) return true;
      if (type === 'PAYMENT') return true;
      return false;
    },
    async fetchReferralStats(params) {
      return await this.$store.dispatch('customer/getReferralStats', params);
    },
    isDateBeforeToday(date) {
      return isBefore(parseISO(date), parseISO(new Date().toISOString().slice(0, 10)));
    },

    socialLink(name, link, usePromotion = true) {
      const title = usePromotion
        ? `Use%20this%20link%20and%20get%20$100-$250%20off%20your%20embroidery%20machine:%20${link}`
        : `${link}`;
      const links = {
        // linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${this.referralLink}&title=${title}&source=ricoma.com`,
        facebook: `http://www.facebook.com/sharer.php?u=${link}&t=${title}`,
        twitter: `https://twitter.com/intent/tweet?text=${title}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${link}`,
        email: `mailto:?Subject=${title}`,
      };
      return links[name];
    },

    async fetchReferralProductsEligible() {
      const { data } = await this.$http.get('/product/referral-products-eligible');
      this.productsEligible = data;
    },
    async fetchCsvExport() {
      const { data } = await this.$http.get('/customer/get_referral_stats', {
        params: { per_page: 100000000 },
      });
      this.csvData = normalizeCustomerReferrals(
        safeNullDataset(data)(['data', 'referred_order_items', 'data']),
      ).filter(d => d.type !== 'STATEMENT');
    },
  },
  icons: [],
  howItWorks: [],
  csvProps: {},
  tColumns: [],
  created() {
    const HIDDEN_COLUMNS_FIELDS = ['referral threshold'];
    const USER_TYPE = isInfluencer(this.$user) ? 'influencer' : 'general';

    /*
     * Table Columns
     */
    const T_COLUMNS = [
      { column: 'customer', isSort: false },
      { column: 'invoice/memo #', isSort: false },
      { column: 'Sales Order #', isSort: false },
      { column: 'date created', isSort: false },
      { column: 'shopify reward', isSort: false },
      { column: 'referral threshold', isSort: false },
      { column: 'order total', isSort: false },
      { column: 'percentage earned', isSort: false },
      { column: 'rewards amount', isSort: false },
      { column: 'total balance', isSort: false, classes: { td: 'font-bold' } },
    ];

    const showColumns = (user, columns) => ({ column }) =>
      isInfluencer(user) || !columns.includes(column);

    this.$options.tColumns = T_COLUMNS.filter(showColumns(this.$user, HIDDEN_COLUMNS_FIELDS));

    /*
     * Intro Section
     */
    this.$options.introHeading = this.$t(`referrals.intro.heading.${USER_TYPE}`);

    /*
     * Icons Section
     */
    const ICONS = [
      { prefix: 'fab', name: 'facebook-f', bg: '#3C5A96', link: 'facebook' },
      // { prefix: 'fab', name: 'facebook-messenger', bg: '#007FFF', link: 'messenger' },
      { prefix: 'fab', name: 'linkedin-in', bg: '#2B7AB2', link: 'linkedin' },
      { prefix: 'fab', name: 'twitter', bg: '#59ADEB', link: 'twitter' },
      { prefix: 'fas', name: 'envelope', bg: '#454545', link: 'email' },
    ];
    this.$options.icons = ICONS;

    /*
     * How It Works Section
     */
    const HOW_IT_WORKS = {
      heading: this.$t('referrals.intro.howItWorks.heading'),
      list: [
        this.$t(`referrals.intro.howItWorks.list.l1-${USER_TYPE}`),
        this.$t(`referrals.intro.howItWorks.list.l2-${USER_TYPE}`),
        this.$t(`referrals.intro.howItWorks.list.l3-${USER_TYPE}`),
      ],
      video: {
        actionText: this.$t('referrals.intro.howItWorks.actionText'),
        thumbnail:
          'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/customer-side/customer-portal-how-to-referrals-intro-thumbnail.png',
        url:
          'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/customer-side/customer-portal-how-to-referrals-intro.mp4',
      },
    };
    this.$options.howItWorks = HOW_IT_WORKS;

    /*
     * Downloadable CSV
     */
    const CSV_FIELDS = [
      'invoice/memo #',
      'date created',
      'shopify reward',
      'referral threshold',
      'order total',
      'percentage earned',
      'rewards amount',
      'type',
      'total balance',
    ];
    const CSV_LABELS = {
      'invoice/memo #': this.$t('referrals.stats.csv.labels.l1'),
      'date created': this.$t('referrals.stats.csv.labels.l2'),
      'shopify reward': this.$t('referrals.stats.csv.labels.l3'),
      'referral threshold': this.$t('referrals.stats.csv.labels.l4'),
      'order total': this.$t('referrals.stats.csv.labels.l5'),
      'percentage earned': this.$t('referrals.stats.csv.labels.l6'),
      'rewards amount': this.$t('referrals.stats.csv.labels.l7'),
      type: this.$t('referrals.stats.csv.labels.l8'),
      'total balance': this.$t('referrals.stats.csv.labels.l9'),
    };

    const showFields = (user, fields) => field => !(isInfluencer(user) && fields.includes(field));
    const showLabels = (labels, label) => ((labels[label] = CSV_LABELS[label]), labels);

    const fields = CSV_FIELDS.filter(showFields(this.$user, HIDDEN_COLUMNS_FIELDS));
    const labels = fields.reduce(showLabels, {});

    this.$options.csvProps = { fields, labels };

    this.fetchCsvExport();
    this.fetchReferralProductsEligible();
  },
};
</script>
